import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import { getHitLabApiMessage } from './hitLabUtils';
import {
  FEEDBACK_REWARD_PASS_REWARD_AMOUNT_JGK,
  FEEDBACK_REWARD_PASS_REWARD_CASHOUT_VALUE_USD,
} from './hitLabRadioFeedbackRewardPassApi';

const INITIAL_ACTIVE_DATA = {
  summary: {},
  rows: [],
};

const INITIAL_RECOVERY_SUMMARY = {
  selectedCount: 0,
  recoveryEligible: 0,
  recoverySent: 0,
  recoverySkipped: 0,
  recoveryFailed: 0,
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatUsd = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Not set';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const cleanRecoveryStatus = (value) =>
  String(value || 'not_eligible')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getRewardAmountJgk = (row) => {
  const amount = Number(row?.rewardAmountJgk);
  return Number.isFinite(amount) && amount > 0
    ? Math.round(amount)
    : FEEDBACK_REWARD_PASS_REWARD_AMOUNT_JGK;
};

const getRewardCashoutValueUsd = (row) => {
  const value = Number(row?.rewardCashoutValueUsd);
  return Number.isFinite(value) && value > 0
    ? value
    : FEEDBACK_REWARD_PASS_REWARD_CASHOUT_VALUE_USD;
};

const getRewardCopy = (row) =>
  `${formatCount(getRewardAmountJgk(row))} JGK / $${formatUsd(
    getRewardCashoutValueUsd(row),
  )}`;

const getProgress = (row) => row?.progress || {};

export default function HitLabRadioListenerFeedbackRecoveryAdmin({
  authConfig,
  refreshKey = 0,
}) {
  const [activeData, setActiveData] = useState(INITIAL_ACTIVE_DATA);
  const [recoverySummary, setRecoverySummary] = useState(INITIAL_RECOVERY_SUMMARY);
  const [hoursOld, setHoursOld] = useState('18');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const rows = useMemo(
    () =>
      (Array.isArray(activeData.rows) ? activeData.rows : []).filter(
        (row) => row?.status === 'started',
      ),
    [activeData.rows],
  );
  const summary = activeData.summary || {};

  const loadRecovery = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    setLoading(true);
    setError('');
    const params = {
      hoursOld,
    };
    return Promise.all([
      axiosConn.get('hitlab/admin/radio/listener-feedback-rewards/active', {
        ...authConfig,
        params,
      }),
      axiosConn.get(
        'hitlab/admin/radio/listener-feedback-rewards/recovery/summary',
        {
          ...authConfig,
          params,
        },
      ),
    ])
      .then(([activeResponse, summaryResponse]) => {
        setActiveData(activeResponse.data?.data || INITIAL_ACTIVE_DATA);
        setRecoverySummary(
          summaryResponse.data?.data || INITIAL_RECOVERY_SUMMARY,
        );
      })
      .catch((err) => {
        setError(
          getHitLabApiMessage(
            err,
            'Active Listener Pass Recovery could not be loaded.',
          ),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authConfig, hoursOld]);

  useEffect(() => {
    void loadRecovery();
  }, [loadRecovery, refreshKey]);

  const runRecovery = async ({ dryRun }) => {
    if (!authConfig || busy) return;
    if (!dryRun) {
      const confirmed = window.confirm(
        'Send listener pass recovery emails to currently eligible started passes only?',
      );
      if (!confirmed) return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await axiosConn.post(
        'hitlab/admin/radio/listener-feedback-rewards/recovery/run',
        {
          dryRun,
          hoursOld,
        },
        authConfig,
      );
      const data = response.data?.data || {};
      setMessage(
        dryRun
          ? `Preview found ${formatCount(data.selectedCount)} eligible started passes.`
          : `Recovery sent ${formatCount(data.sentCount)} emails. ${formatCount(
              data.failedCount,
            )} failed.`,
      );
      await loadRecovery();
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          dryRun
            ? 'Recovery preview could not be run.'
            : 'Recovery emails could not be sent.',
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="hitlab-radio-feedback-recovery-admin"
      aria-label="Active Listener Pass Recovery"
    >
      <div className="hitlab-radio-feedback-rewards-admin-header">
        <div>
          <p className="hitlab-kicker">ACTIVE LISTENER PASSES</p>
          <h2>Active Listener Pass Recovery</h2>
          <p>
            Explicit started listener feedback passes that have not submitted
            yet. Recovery emails only target started passes.
          </p>
        </div>
        <div className="hitlab-radio-feedback-recovery-actions">
          <label>
            Idle hours
            <input
              type="number"
              min="1"
              max="168"
              value={hoursOld}
              onChange={(event) => setHoursOld(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="hitlab-secondary-btn"
            disabled={loading || busy}
            onClick={loadRecovery}
          >
            {loading ? 'Loading...' : 'Refresh active'}
          </button>
          <button
            type="button"
            className="hitlab-secondary-btn"
            disabled={loading || busy}
            onClick={() => runRecovery({ dryRun: true })}
          >
            Preview eligible
          </button>
          <button
            type="button"
            className="hitlab-primary-btn"
            disabled={loading || busy}
            onClick={() => runRecovery({ dryRun: false })}
          >
            {busy ? 'Working...' : 'Send recovery emails'}
          </button>
        </div>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {message ? <div className="hitlab-alert">{message}</div> : null}

      <div className="hitlab-radio-feedback-rewards-summary">
        <article>
          <span>Total started</span>
          <strong>{formatCount(summary.totalStarted)}</strong>
        </article>
        <article>
          <span>Ready to submit</span>
          <strong>{formatCount(summary.readyToSubmit)}</strong>
        </article>
        <article>
          <span>Recovery eligible</span>
          <strong>{formatCount(recoverySummary.recoveryEligible)}</strong>
        </article>
        <article>
          <span>Recovery sent</span>
          <strong>{formatCount(recoverySummary.recoverySent)}</strong>
        </article>
        <article>
          <span>Recovery failed</span>
          <strong>{formatCount(recoverySummary.recoveryFailed)}</strong>
        </article>
      </div>

      {rows.length ? (
        <div className="hitlab-radio-feedback-recovery-list">
          {rows.map((row) => {
            const progress = getProgress(row);
            return (
              <article key={row.id} className="hitlab-radio-feedback-reward-row">
                <div className="hitlab-radio-feedback-reward-row-main">
                  <div>
                    <strong>{row.contactEmail || row.player || 'Started listener'}</strong>
                    <span>
                      Started {formatDate(row.passStartedAt)} - Source{' '}
                      {row.source || 'direct'}
                    </span>
                    <small>Last activity {formatDate(row.lastActivityAt)}</small>
                  </div>
                  <div>
                    <span>Progress</span>
                    <strong>
                      {formatCount(progress.distinctTrackCount)} /{' '}
                      {formatCount(progress.threshold || 5)}
                    </strong>
                    <small>{progress.eligible ? 'Unlocked' : 'Listening'}</small>
                  </div>
                </div>
                <div className="hitlab-radio-feedback-reward-status">
                  <span>{cleanRecoveryStatus(row.recoveryStatus)}</span>
                  {row.recoveryReason ? <span>{row.recoveryReason}</span> : null}
                  <span>{getRewardCopy(row)}</span>
                  {row.recoveryEmailSentAt ? (
                    <span>Sent {formatDate(row.recoveryEmailSentAt)}</span>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="hitlab-alert">
          No explicit started Listener Feedback Reward passes are active.
        </div>
      )}
    </section>
  );
}
